import React from "react";
import {
  NumericValidator,
  Question,
  QuestionFactory,
  Serializer,
} from "survey-core";
import {
  ReactQuestionFactory,
  SurveyQuestionElementBase,
} from "survey-react-ui";

const QUESTION_TYPE = "ageRangeYear";

let isRegistered = false;

class QuestionAgeRangeYearModel extends Question {
  constructor(name) {
    super(name);
    this.syncValidators();
  }

  getType() {
    return QUESTION_TYPE;
  }

  get placeholder() {
    return this.getPropertyValue("placeholder", "Age Year");
  }

  set placeholder(val) {
    this.setPropertyValue("placeholder", val);
  }

  get minValue() {
    return this.getPropertyValue("minValue");
  }

  set minValue(val) {
    this.setPropertyValue("minValue", val);
    this.syncValidators();
  }

  get maxValue() {
    return this.getPropertyValue("maxValue");
  }

  set maxValue(val) {
    this.setPropertyValue("maxValue", val);
    this.syncValidators();
  }

  get step() {
    return this.getPropertyValue("step", 1);
  }

  set step(val) {
    this.setPropertyValue("step", val);
  }

  syncValidators() {
    const minRaw = this.minValue;
    const maxRaw = this.maxValue;

    const hasMin = minRaw !== undefined && minRaw !== null && minRaw !== "";
    const hasMax = maxRaw !== undefined && maxRaw !== null && maxRaw !== "";

    // Preserve any other validators the user may add in the creator.
    const existing = Array.isArray(this.validators) ? this.validators : [];
    const otherValidators = existing.filter(
      (v) =>
        (typeof v?.getType === "function" ? v.getType() : "") !==
        "numericvalidator",
    );

    if (!hasMin && !hasMax) {
      this.validators = otherValidators;
      return;
    }

    const min = hasMin ? Number(minRaw) : null;
    const max = hasMax ? Number(maxRaw) : null;

    this.validators = [...otherValidators, new NumericValidator(min, max)];
  }

  setYearValue(raw) {
    // Keep the value either a number or null.
    if (raw === "") {
      this.value = null;
      return;
    }

    // Enforce digits-only (no e/E, no decimals, no +/-).
    if (!/^\d+$/.test(raw)) return;

    this.value = Number(raw);
  }
}

class SurveyQuestionAgeRangeYear extends SurveyQuestionElementBase {
  get question() {
    return this.props.question;
  }

  handleChange = (e) => {
    if (this.question.isReadOnly) return;
    this.question.setYearValue(e.target.value);
  };

  renderElement() {
    const q = this.question;

    const inputClassName = [q.cssClasses?.text, q.cssClasses?.textInput]
      .filter(Boolean)
      .join(" ");

    const value =
      q.value === undefined || q.value === null ? "" : String(q.value);

    return (
      <input
        className={inputClassName}
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: "6px 12px",
          borderRadius: "4px",
          border: "1px solid #ced4da",
        }}
        type="number"
        inputMode="numeric"
        pattern="[0-9]*"
        value={value}
        placeholder={q.placeholder || undefined}
        disabled={q.isReadOnly}
        readOnly={q.isReadOnly}
        min={q.minValue ?? undefined}
        max={q.maxValue ?? undefined}
        step={q.step || 1}
        onChange={this.handleChange}
      />
    );
  }
}

export function registerAgeRangeYearQuestion() {
  if (isRegistered) return;
  isRegistered = true;

  Serializer.addClass(
    QUESTION_TYPE,
    [
      {
        name: "name",
        type: "text",
        category: "general",
        visible: true,
        isRequired: true,
        isLocalizable: false,
        onSetValue: function (obj, value) {
          obj.setPropertyValue("name", value);
        },
        onGetValue: function (obj) {
          return obj.getPropertyValue("name");
        },
      },
      { name: "placeholder:text", category: "general", default: "Age Year" },
      {
        name: "minValue:number",
        category: "general",
        displayName: "Min Value",
      },
      {
        name: "maxValue:number",
        category: "general",
        displayName: "Max Value",
      },
      {
        name: "step:number",
        category: "general",
        default: 1,
        displayName: "Step",
      },
    ],
    () => new QuestionAgeRangeYearModel(""),
    "question",
  );

  QuestionFactory.Instance.registerQuestion(
    QUESTION_TYPE,
    (name) => new QuestionAgeRangeYearModel(name),
  );

  ReactQuestionFactory.Instance.registerQuestion(QUESTION_TYPE, (props) => {
    return React.createElement(SurveyQuestionAgeRangeYear, props);
  });
}

// Auto-register on import (convenient for SurveyBuilder usage).
registerAgeRangeYearQuestion();
